/**
 * "Who is this, exactly?" — the panel behind the ⓘ on the checkout screen.
 *
 * Exists because the checkout page deliberately shows almost nothing: a donor who is *sure* should
 * not have to read past the amount, and a donor who is *unsure* needs somewhere to go that is not
 * "abandon the gift and search the web". One tap, and one tap back.
 *
 * The EIN is presented as a bare identifier. There is no sentence here about tax deductibility,
 * exempt status, or what a donor may claim — an EIN is not evidence of any of those, and writing
 * one would be a legal statement made by a UI decision rather than by counsel (DEC-001, DEC-011).
 */
import { useEffect, useState } from "react";
import { useDialogFocus } from "@givehere/ui";
import { formatMoney, type OrganizationDetailResponse } from "@givehere/contracts";
import { fetchOrganizationDetail } from "./api.js";
import { OrgFacts } from "./OrgFacts.js";
import { OrgMark } from "./OrgMark.js";

export function OrgInfoPanel({
  organizationId,
  onDismiss,
}: {
  organizationId: string;
  onDismiss: () => void;
}) {
  const panelRef = useDialogFocus<HTMLDivElement>();
  const [detail, setDetail] = useState<OrganizationDetailResponse | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    fetchOrganizationDetail(organizationId)
      .then((value) => live && setDetail(value))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [organizationId]);

  // Escape closes it. A panel that can only be dismissed by finding a small button is a trap on
  // the one screen where a donor is deciding whether to trust us.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  const organization = detail?.organization;

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="About this organization">
      <div className="sheet__panel sheet__panel--tall lattice-edge" ref={panelRef} tabIndex={-1}>
        <button type="button" className="sheet__close" aria-label="Go back" onClick={onDismiss}>
          ✕
        </button>

        {failed && <p className="notice notice--error">Could not load these details.</p>}
        {!detail && !failed && <p className="notice">Loading…</p>}

        {organization && (
          <>
            <header className="info__head">
              <OrgMark
                displayName={organization.displayName}
                logoUrl={organization.logoUrl}
                className="org__mark"
                size={56}
              />
              <div>
                <h2 className="info__name" dir="auto">
                  {organization.displayName}
                </h2>
                {organization.locality && (
                  <p className="org__locality" dir="auto">
                    {organization.locality}
                  </p>
                )}
              </div>
            </header>

            <p
              className={organization.canAcceptDonations ? "badge badge--ok" : "badge badge--muted"}
            >
              {organization.verificationLabel}
            </p>

            {organization.description && (
              <p className="info__body" dir="auto">
                {organization.description}
              </p>
            )}

            {/* The four facts, and the one anchor treatment that goes with them. Shared with
                the organization hub at `/o/<id>`, which shows the same record — see `OrgFacts`
                for why the website link is not written twice. */}
            <OrgFacts organization={organization} />

            {detail.event && (
              <section className="info__event">
                <h3 className="info__subhead" dir="auto">
                  {detail.event.name}
                </h3>
                {detail.event.purpose && <p className="info__body">{detail.event.purpose}</p>}
                {detail.event.description && (
                  <p className="info__body" dir="auto">
                    {detail.event.description}
                  </p>
                )}
                <p className="info__goal">Goal {formatMoney(detail.event.goal)}</p>
                {/*
                  "See the live total" described the hall's wall, which is what `/e/<id>` used to
                  open. It now opens the donor's own event page, so the label says that instead —
                  a link whose words promise a different screen than the one it opens is a small
                  lie the product tells every time somebody taps it.
                */}
                <a className="link link--block" href={`/e/${detail.event.id}`}>
                  Open the event page
                </a>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
